<template>
  <div :id="pSelectIdPrefix + 'SelectContainer'" :class="[{'form-group': pStyleClassFormGroup}, {'has-error': hasFieldErrors(pErrorKey)}]">
    <template v-if="pLabelVisible">
      <div v-if="hasFieldErrors(pErrorKey)">
        <span v-for="error in getFieldErrors(pErrorKey)" class="help-block bs3-margin-top-reset bs3-margin-bottom5" :key="error">{{ error }}</span>
      </div>
      <label v-else :for="pSelectIdPrefix + 'Select'">
        <span v-if="pRequired" class="required">*</span> {{ pDictionaryKeyLabel | i18nText(pDictionary) }}:
      </label>
    </template>
    <select :id="pSelectIdPrefix + 'Select'"
            ref="select"
            :name="pInputName"
            v-model="selectedOptions"
            data-live-search="true"
            :data-container="pSelectContainer || 'false'"
            :data-deselect-all-text="'dictionary.actions.select.none' | i18nText(pDictionary)"
            :data-select-all-text="'dictionary.actions.select.all' | i18nText(pDictionary)"
            :data-count-selected-text="pDictionaryKeyCount | i18nText(pDictionary)"
            :data-none-selected-text="pDictionaryKeySelect | i18nText(pDictionary)"
            data-selected-text-format="count > 3"
            data-actions-box="true"
            data-width="pSelectWidth"
            :data-style="pSelectStyleClass"
            :class="'form-control selectpicker ' + pStyleClass" :multiple="pMultiple"
            :disabled="disabled">
      <option v-if="noneOptionVisible" :value="pRefValueBased ? '' : 0">{{ pDictionaryKeySelect | i18nText(pDictionary) }}</option>
      <template v-for="ref in pRefList">
        <optgroup v-if="ref.childDataRefs.length > 0"
                  :label="i18nRefName(ref, pLanguage)"
                  :key="ref.id">
          <option v-for="childRef in ref.childDataRefs"
                  :key="getOptionValue(childRef)"
                  :value="getOptionValue(childRef)"
                  :selected="isOptionSelected(childRef)"
                  :data-tokens="i18nRefName(ref, pLanguage) + ',' + i18nRefName(childRef, pLanguage)">
            {{ i18nRefName(childRef, pLanguage) }}
          </option>
        </optgroup>
        <option v-else :value="getOptionValue(ref)"
                :selected="isOptionSelected(ref)"
                :data-tokens="i18nRefName(ref, pLanguage)"
                :key="getOptionValue(ref)">
          {{ i18nRefName(ref, pLanguage) }}
        </option>
      </template>
    </select>
    <template v-if="!pLabelVisible">
          <span v-for="(error, errorIndex) in getFieldErrors(pErrorKey)"
                :key="'errorList' + pSelectIdPrefix + 'Select' + errorIndex" class="help-block">{{ error }}</span>
    </template>
    <slot name="form-select-hint"></slot>
  </div>
</template>

<script>
import BaseMixin from "@/mixins/base-mixin";
import FormMixin from "@/mixins/form-mixin";
import FormatMixin from "@/mixins/format-mixin";
import refHelpers from '@/helpers/ref-helpers'
import stringHelpers from "@/helpers/string-helpers";

export default {
  name: "FormSelect",
  mixins: [BaseMixin, FormMixin, FormatMixin],
  model: {
    prop: 'pSelectedOptions',
    event: 'change'
  },
  props: {
    pSelectedOptions: [String, Number, Array], // Array when pMultiple=true; Number otherwise
    pRefValueBased: {
      type: Boolean,
      default: function () {
        return false;
      }
    },
    pUrls: Object,
    pErrorKey: String,
    pRequired: Boolean,
    pInputName: String,
    pRefList: {
      type: Array,
      default: function () {
        return []
      }
    },
    pSelectIdPrefix: {
      type: String,
      required: true
    },
    pSelectContainer: {
      default: function () {
        return ""
      }
    },
    pMultiple: {
      type: Boolean,
      default: function () {
        return false;
      }
    },
    pDisabled: Boolean,
    pLabelVisible: {
      type: Boolean,
      default: function () {
        return true
      }
    },
    pStyleClass: {
      type: String,
      default: function () {
        return ''
      }
    },
    pStyleClassFormGroup: {
      type: Boolean,
      default: function () {
        return true
      }
    },
    pSelectStyleClass: {
      type: String,
      default: function () {
        return 'btn-default'
      }
    },
    pSelectWidth: {
      type: String,
      default: function () {
        return 'false'
      }
    },
    pSelectNoneOptionVisible: {
      type: Boolean,
      default: function() {
        return true;
      }
    },
    pDictionaryKeyLabel: String,
    pDictionaryKeyCount: String,
    pDictionaryKeySelect: String
  },
  data: function () {
    return {
      selectedOptions: this.pSelectedOptions
    }
  },
  methods: {
    getOptionValue: function(ref) {
      if(this.pRefValueBased) {
        return refHelpers.getRefValue(ref, null)
      } else {
        return refHelpers.getId(ref, null)
      }
    },
    getOptionValues: function(refs) {
      if(this.pRefValueBased) {
        return refHelpers.getRefValues(refs, [])
      } else {
        return refHelpers.getIds(refs, [])
      }
    },
    isOptionSelected: function (option) {
      if(this.pMultiple)
      {
        return this.selectedOptions.includes(option);
      }
      else
      {
        return this.selectedOptions === option;
      }
    }
  },
  computed: {
    disabled: function () {
      return this.pDisabled || this.pRefList.length === 0;
    },
    noneOptionVisible: function() {
      return !this.pMultiple && this.pSelectNoneOptionVisible
    }
  },
  watch: {
    pSelectedOptions: function (newSelectedOptions) {
      console.debug("[FormSelect] pSelectedOptions changed: " + newSelectedOptions);
      this.selectedOptions = newSelectedOptions;
    },
    selectedOptions: function (newSelectedOptions) {
      console.debug("[FormSelect] selectedOptions changed: " + newSelectedOptions);
      this.$emit('change', newSelectedOptions)
    },
    pRefList: function (newRefList) {

      let _component = this;

      let optionValues = [];
      $.each(newRefList, function(index, ref) {
        if(ref.childDataRefs.length > 0) {
          optionValues = optionValues.concat(_component.getOptionValues(ref.childDataRefs));
        } else {
          optionValues.push(_component.getOptionValue(ref));
        }
      })

      console.debug("Retlist changed:", newRefList, optionValues, this.selectedOptions);

      if(this.pMultiple)
      {
        let newSelectedOptions = optionValues.filter(optionValue => this.selectedOptions.includes(optionValue));
        if(!stringHelpers.isArrayEquals(this.selectedOptions, newSelectedOptions))
        {
          // We only change this.selecteIds if there's actually something changed.
          // as this triggers an change event.
          this.selectedOptions = newSelectedOptions;
        }
      }
      else
      {
        this.selectedOptions = optionValues.includes(this.selectedOptions) ? this.selectedOptions : null;
      }
    },
    disabled: function () {
      let _component = this;
      let $select = $(_component.$refs.select);
      $select.selectpicker('refresh');
    }
  },
  mounted: function () {
    let _component = this;
    let $select = $(_component.$refs.select);
    $select.selectpicker();
    $select.on('changed.bs.select', function (e, clickedIndex) {
      let $bsSelect = $(e.target);
      let $clickedOption = $bsSelect.find('option').eq(clickedIndex);

      console.debug("Clicked option = " + $clickedOption.val());

      if($clickedOption.val() <= 0)
      {
        console.debug("Clicked option A");
        $.each($bsSelect.find(":selected").not($clickedOption), function (index, option) {
          let $option = $(option);
          $option.prop('selected', false);
        });
      }
      else
      {
        console.debug("Clicked option B");
        $.each($bsSelect.find(":selected").not($clickedOption), function (index, option) {
          let $option = $(option);
          if($option.val() <= 0)
          {
            $option.prop('selected', false);
          }
        });
      }
      $bsSelect.selectpicker('refresh');
    });
  },
  updated: function () {
    let _component = this;
    let $select = $(_component.$refs.select);
    $select.selectpicker('refresh');
  },
  destroyed: function () {
    let _component = this;
    let $select = $(_component.$refs.select);
    $select.selectpicker('destroy');
  }
}
</script>

<style scoped>

</style>